import { type ShopfrontDispatch } from "../../../_Bootstrap/hooks";
import { store } from "../../../_Bootstrap/RoutingAndStore";
import {
    addNoteToSale,
    addOrderReference,
    cancelSale,
    overrideSaleMetaData,
    updateProduct,
} from "../../../Actions/SellScreen/SellScreenActions";
import { addCustomerToSale, removeCustomerFromSale } from "../../../Actions/SellScreen/SellScreenCustomerActions";
import { addPaymentToSale, type SaleAddPaymentOptions } from "../../../Actions/SellScreen/SellScreenPaymentActions";
import { addProductToSale, removeProductFromSale } from "../../../Actions/SellScreen/SellScreenProductActions";
import { type SalePaymentStatus, type SaleProduct } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { uppercaseFirst } from "../../Generic/Strings";

interface EmbeddedSaleUpdateChanges {
    PRODUCT_ADD: {
        id: string;
        quantity: number;
        price?: number;
        consolidate?: boolean;
        indexAddress?: Array<number>;
        metaData: Record<string, unknown>;
    };
    PRODUCT_REMOVE: {
        id: string;
        indexAddress: Array<number>;
    };
    PAYMENT_ADD: {
        id: string;
        amount: number;
        cashout?: number | boolean;
        status?: SalePaymentStatus;
        options?: Partial<Exclude<SaleAddPaymentOptions, "status" | "cashout">>;
    };
    PAYMENT_REVERSE: {
        id: string;
        amount: number;
        cashout?: number;
        status?: SalePaymentStatus;
    };
    SALE_CANCEL: {};
    CUSTOMER_ADD: {
        id: string;
    };
    CUSTOMER_REMOVE: {};
    SALE_EXTERNAL_NOTE: {
        note: string;
        append?: boolean;
    };
    SALE_INTERNAL_NOTE: {
        note: string;
        append?: boolean;
    };
    SALE_ORDER_REFERENCE: {
        reference: string;
    };
    SALE_META_DATA: {
        metaData: Record<string, unknown>;
    };
    PRODUCT_UPDATE: {
        id: string;
        indexAddress: Array<number>;
        metaData?: Record<string, unknown>;
        quantity?: number;
        price?: number;
    };
}

export interface EmbeddedSaleUpdateData {
    properties: [
        keyof EmbeddedSaleUpdateChanges, // Change type
        EmbeddedSaleUpdateChanges[keyof EmbeddedSaleUpdateChanges], // Change data
    ];
    events: [];
    type: "SaleUpdate";
}

export class EmbeddedSaleUpdate {
    protected static withinPreFinishSalePipeline = false;
    protected change: keyof EmbeddedSaleUpdateChanges;
    protected data: EmbeddedSaleUpdateChanges[keyof EmbeddedSaleUpdateChanges];

    constructor(dataFromEmbedded: EmbeddedSaleUpdateData) {
        this.change = dataFromEmbedded.properties[0];
        this.data = dataFromEmbedded.properties[1];
    }

    protected getProductFromSale(
        data: EmbeddedSaleUpdateChanges["PRODUCT_REMOVE" | "PRODUCT_UPDATE"]
    ): SaleProduct | false {
        // Check to make sure the ID matches the product at the address
        const state = store.getState();
        const sale = state.sellscreen.sale;
        let product: SaleProduct | false = false;

        for(let i = 0, l = data.indexAddress.length; i < l; i++) {
            product = sale.products[data.indexAddress[i]];

            if(typeof product === "undefined") {
                console.error("Embedded: Invalid index address specified (not found)");

                return false;
            }
        }

        if(!product || product.uuid !== data.id) {
            console.error("Embedded: Invalid index address specified (product doesn't match)");

            return false;
        }

        return product;
    }

    protected applyProductAdd(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PRODUCT_ADD"];

        (store.dispatch as ShopfrontDispatch)(addProductToSale(data.id, {
            quantity    : data.quantity,
            price       : data.price,
            consolidate : data.consolidate,
            indexAddress: data.indexAddress,
            metaData    : data.metaData || {},
        }));
    }

    protected applyProductRemove(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PRODUCT_REMOVE"];

        // No matching product found in sale
        if(!this.getProductFromSale(data)) {
            return;
        }

        // Remove the product from the sale
        (store.dispatch as ShopfrontDispatch)(removeProductFromSale(data.indexAddress));
    }

    protected applyPaymentAdd(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PAYMENT_ADD"];

        (store.dispatch as ShopfrontDispatch)(addPaymentToSale(data.id, data.amount, {
            ...(data.options ?? {}),
            cashout : data.cashout,
            status  : data.status,
            // Specifying the `vouchers` option allows the Embedded API to overpay the sale (useful for triggering
            // refunds on already made payments due to a voucher being added that has since been rejected)
            vouchers: data.options?.vouchers ?? EmbeddedSaleUpdate.withinPreFinishSalePipeline,
        }));
    }

    protected applyPaymentReverse(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PAYMENT_REVERSE"];

        const sale = store.getState().sellscreen.sale;

        if(!sale) {
            return;
        }

        let related: SaleAddPaymentOptions["related"] = undefined;

        for(let i = 0, l = sale.payments.length; i < l; i++) {
            const payment = sale.payments[i];

            if(
                payment.status !== "completed" ||
                payment.reverted ||
                payment.method !== data.id ||
                payment.amount !== data.amount ||
                payment.cashout !== data.cashout
            ) {
                continue;
            }

            related = {
                ...payment,
                index: i,
            };
        }

        (store.dispatch as ShopfrontDispatch)(addPaymentToSale(data.id, data.amount * -1, {
            cashout  : typeof data.cashout === "number" ? data.cashout * -1 : undefined,
            status   : data.status,
            refund   : true,
            reverting: true,
            related,
        }));
    }

    protected applySaleCancel(): void {
        // const data = this.data as EmbeddedSaleUpdateChanges["SALE_CANCEL"];

        (store.dispatch as ShopfrontDispatch)(cancelSale());
    }

    protected applyCustomerAdd(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["CUSTOMER_ADD"];

        // NOTE: Currently overrides the current customer
        (store.dispatch as ShopfrontDispatch)(addCustomerToSale(data.id, undefined, true));
    }

    protected applyCustomerRemove(): void {
        // const data = this.data as EmbeddedSaleUpdateChanges["CUSTOMER_REMOVE"];

        (store.dispatch as ShopfrontDispatch)(removeCustomerFromSale());
    }

    protected applySaleExternalNote(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["SALE_EXTERNAL_NOTE"];

        let note = "";

        if(data.append) {
            const state = store.getState();

            note = state.sellscreen.sale.notes.sale;
        }

        note = `${note}${data.note}`;

        (store.dispatch as ShopfrontDispatch)(addNoteToSale("sale", note));
    }

    protected applySaleInternalNote(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["SALE_INTERNAL_NOTE"];

        let note = "";

        if(data.append) {
            const state = store.getState();

            note = state.sellscreen.sale.notes.internal;
        }

        note = `${note}${data.note}`;

        (store.dispatch as ShopfrontDispatch)(addNoteToSale("internal", note));
    }

    protected applySaleOrderReference(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["SALE_ORDER_REFERENCE"];

        (store.dispatch as ShopfrontDispatch)(addOrderReference(data.reference));
    }

    protected applySaleMetaData(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["SALE_META_DATA"];

        (store.dispatch as ShopfrontDispatch)(overrideSaleMetaData(data.metaData));
    }

    protected applyProductUpdate(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PRODUCT_UPDATE"];

        const product = this.getProductFromSale(data);

        // No matching product found in sale
        if(!product) {
            return;
        }

        const dataToUpdate: Partial<SaleProduct> = {};

        if(data.quantity) {
            dataToUpdate.quantity = data.quantity;
        }

        if(data.price) {
            dataToUpdate.prices = {
                ...product.prices,
                price: data.price,
            };
        }

        if(data.metaData) {
            dataToUpdate.metaData = data.metaData;
        }

        (store.dispatch as ShopfrontDispatch)(updateProduct(
            data.indexAddress,
            dataToUpdate
        ));
    }

    public apply(): void {
        const change = this.change.toString().toLowerCase();
        const split = change.split("_");

        let method = "apply";

        for(let i = 0, l = split.length; i < l; i++) {
            method = `${method}${uppercaseFirst(split[i])}`;
        }

        if(typeof this[method as keyof EmbeddedSaleUpdate] !== "function") {
            console.error("Unknown embedded sale update requested", this.change, this.data);

            return;
        }

        this[method as keyof EmbeddedSaleUpdate]();
    }

    /**
     * Set whether we're within the pre-finish sale pipeline
     */
    public static setWithinPreFinishSalePipeline(withinPipeline: boolean): void {
        this.withinPreFinishSalePipeline = withinPipeline;
    }
}
